// Capacity selectors — Option A: video pauses chat capacity entirely.
//
// Hard rules (from dev-docs/PROJECT.md):
//   - Video is a hard max of 1
//   - Chat cap is configurable per agent (1–5)
//   - While on a video call, the agent CANNOT accept any new chats; existing
//     chats stay open but render the "agent is in a call" banner.

import type { AgentId } from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { useConversationsStore, type Conversation } from '../stores/conversations';

export interface CapacityView {
  chatCap: number;
  currentChats: number;
  videoStatus: 'available' | 'on_call' | 'opted-out';
  canTakeChat: boolean;
  canTakeVideo: boolean;
  pausedSessionIds: string[];
}

/**
 * An "active chat" for capacity is one the current agent is currently
 * handling — owned by 'agent' and ownerAgentId === me. AI-owned conversations,
 * historical conversations (post-hydration), and chats owned by another
 * agent don't consume MY chat capacity.
 */
function activeChats(
  byId: Map<string, Conversation>,
  me: AgentId | undefined,
  dismissed: Set<string>,
): Conversation[] {
  if (!me) return [];
  return Array.from(byId.values()).filter(
    (c) =>
      c.medium === 'chat' &&
      c.ownedBy === 'agent' &&
      c.ownerAgentId === me &&
      // 0-message conversations are "preview windows" — the agent clicked
      // through to a visitor row but hasn't sent anything yet. They shouldn't
      // burn a capacity slot. The soft lock on the visitor row is enough
      // signal to other agents.
      c.messages.length > 0 &&
      // Dismissed conversations don't count either — the agent has explicitly
      // (or via hydration default) put them aside. Live activity un-dismisses
      // them, at which point they count again.
      !dismissed.has(c.sessionId),
  );
}

function videoConversation(byId: Map<string, Conversation>, me: AgentId | undefined): Conversation | undefined {
  if (!me) return undefined;
  return Array.from(byId.values()).find((c) => c.inVideo && c.ownerAgentId === me);
}

/** Hook for reactive consumers. */
export function useCapacity(): CapacityView {
  const chatCap = useAgentStore((s) => s.chatCap);
  const availability = useAgentStore((s) => s.availability);
  const acceptsVideo = useAgentStore((s) => s.acceptsVideo);
  const me = useAgentStore((s) => s.current?.agentId);
  const byId = useConversationsStore((s) => s.byId);
  const dismissed = useConversationsStore((s) => s.dismissed);

  const chats = activeChats(byId, me, dismissed);
  const video = videoConversation(byId, me);
  const onCall = !!video;
  const offline = availability !== 'available';
  const videoStatus: CapacityView['videoStatus'] = onCall
    ? 'on_call'
    : acceptsVideo
      ? 'available'
      : 'opted-out';

  return {
    chatCap,
    currentChats: chats.length,
    videoStatus,
    // Chat is unbounded — agents serve many visitors concurrently. Only an
    // active video call or being away/offline blocks new chats. Video is
    // opt-in per agent and hard-capped at 1 while in a call.
    canTakeChat: !offline && !onCall,
    canTakeVideo: !offline && !onCall && acceptsVideo,
    pausedSessionIds: onCall ? chats.map((c) => c.sessionId as unknown as string) : [],
  };
}

/** Imperative reader for non-React call sites. */
export function readCapacity(): CapacityView {
  const chatCap = useAgentStore.getState().chatCap;
  const availability = useAgentStore.getState().availability;
  const acceptsVideo = useAgentStore.getState().acceptsVideo;
  const me = useAgentStore.getState().current?.agentId;
  const byId = useConversationsStore.getState().byId;
  const dismissed = useConversationsStore.getState().dismissed;
  const chats = activeChats(byId, me, dismissed);
  const video = videoConversation(byId, me);
  const onCall = !!video;
  const offline = availability !== 'available';
  const videoStatus: CapacityView['videoStatus'] = onCall
    ? 'on_call'
    : acceptsVideo
      ? 'available'
      : 'opted-out';
  return {
    chatCap,
    currentChats: chats.length,
    videoStatus,
    canTakeChat: !offline && !onCall,
    canTakeVideo: !offline && !onCall && acceptsVideo,
    pausedSessionIds: onCall ? chats.map((c) => c.sessionId as unknown as string) : [],
  };
}

export type CapacityBadgeTone = 'free' | 'chat-full' | 'on-video' | 'inactive';

export function capacityTone(v: CapacityView, availability: 'available' | 'away' | 'offline'): CapacityBadgeTone {
  if (availability !== 'available') return 'inactive';
  if (v.videoStatus === 'on_call') return 'on-video';
  return 'free';
}
